import './buttons.css';
import { Portal } from 'react-portal';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import postal from 'postal';
import Menu, { IfmsMenuItem } from './menu/Menu';
import PrimaryInverseButton from './PrimaryInverseButton';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { replaceJobMenuMetadata } from '../features/jobMenuSlice';
import { replaceScreenStackMetadata } from '../features/screenStackSlice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { formatDate } from '../dateUtils';
import { FilterStorageVersion } from '../filtering';
import fastStorage from '../fastStorage';
import _ from 'lodash';

interface IStandAloneInfoModel {
  index: number
}

export interface TooltipButtonItem {
  [x: string]: any,
  label: React.ReactNode | string | undefined,
};
export const borderColors = ['#0079A4', '#B5038F', '#FF3300', '#89C064', '#00B0F0', '#007BFF'];

export function splitMenuItemsIntoSections(tooltipButtonItems: TooltipButtonItem[]): IfmsMenuItem[][][] {
  const result: IfmsMenuItem[][][] = [[[]]]
  tooltipButtonItems.forEach((item, index) => {
    result[result.length - 1][result[result.length - 1].length - 1].push({
      text: item.label,
      selected: false,
      index,
      icon: item?.icon ?? null,
    });
  })
  return result;
}

function renderDateRangeValue(x: any) {
  const parts = x.value ? x.value.split('~') : [];
  const from = parts.length > 0 ? formatDate(parts[0].trim()) : '';
  const to = parts.length > 1 ? formatDate(parts[1].trim()) : '';

  return `${formatDate(from)} ~ ${formatDate(to)}`;
}

function renderValue(x: any) {
  let result = ''

  if (x.type === 'bool')
    result = x.value ? 'Checked' : 'Unchecked';
  else if (x.type === 'date')
    result = renderDateRangeValue(x);
  else
    result = x.value === null ? '' : x.value

  return result;
}

function TooltipButton({
  jobMenuLabel,
  jobMenuUrl,
  jobMenuMetadata,
  standAloneInfo,
  isActiveMenu = false,
  screenFilter,
}: {
  jobMenuLabel: string,
  jobMenuUrl: string,
  jobMenuMetadata?: any,
  standAloneInfo?: IStandAloneInfoModel,
  children?: React.ReactChild | React.ReactNode | string | null | undefined,
  isActiveMenu: boolean
  screenFilter?: any
}) {
  const storage = useAppSelector(x => x.storage);
  const channel = postal.channel('ifms-filter');

  const [menuVisible, _setMenuVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        }
      }],
    placement: 'bottom',
  });

  const dispatch = useAppDispatch();
  const filterOrdersStorageKey = `filter-order-${FilterStorageVersion}-${jobMenuUrl}${standAloneInfo ? `-${standAloneInfo.index}` : ''}`;
  const filterOrderStorage = storage[filterOrdersStorageKey];

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef(false);

  function getSearchTooltipLabel(filterOrder: any, url: string, screenFilter: any) {
    let result: TooltipButtonItem[] = [];
    if ((!filterOrder || filterOrder.length <= 0) && (screenFilter && screenFilter.listType === 0 && !screenFilter.listType2 || screenFilter && screenFilter.listType2 === 0 && !screenFilter.listType))
      return result;

    if (url === '/common/joblistview/btLoadPlan/load') {
      if (screenFilter.listType > 0) {
        let filterType = '';
        switch (screenFilter.listType) {
          case 1:
            filterType = 'Not Closed'
            break;
          case 2:
            filterType = 'Not Departed'
            break;
          case 3:
            filterType = 'Not Arrived'
            break;
          case 4:
            filterType = 'Closed'
            break;
          case 5:
            filterType = 'Completed'
            break;
          case 6:
            filterType = 'Not Completed'
            break;
          case 7:
            filterType = 'Dispatched'
            break;
          case 8:
            filterType = 'In-Transit'
            break;
          case 9:
            filterType = 'Delivered'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });
      }
    }

    else if (url === '/common/joblistview/btOrder/order') {
      if (screenFilter.listType > 0) {
        let filterType = '';
        switch (screenFilter.listType) {
          case 1:
            filterType = 'Not Closed'
            break;
          case 2:
            filterType = 'Not Departed'
            break;
          case 3:
            filterType = 'Not Planned (by Vessel ETA)'
            break;
          case 4:
            filterType = 'Closed'
            break;
          case 5:
            filterType = 'Completed'
            break;
          case 6:
            filterType = 'Not Completed'
            break;
          case 11:
            filterType = 'IT Not Validated'
            break;
          case 12:
            filterType = 'Departed'
            break;
          case 7:
            filterType = 'Arrived'
            break;
          case 8:
            filterType = 'Not Invoiced'
            break;
          case 9:
            filterType = 'Not Invoiced w/Load #'
            break;
          case 10:
            filterType = 'Not Paid Yet'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });
      }
    }

    else if (url === '/common/joblistview/btLocalDO/localDO') {
      if (screenFilter.listType > 0) {
        let filterType = '';
        switch (screenFilter.listType) {
          case 1:
            filterType = 'Not Closed'
            break;
          case 2:
            filterType = 'Not Picked up yet'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });
      }
    }

    else if (url === '/common/joblistview/OCBO/orchidBaro') {
      if (screenFilter.listType > 0) {
        let filterType = 'Customer / U.Cnee';
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
      if (screenFilter.listType2 > 0) {
        let filterType = '';
        switch (screenFilter.listType2) {
          case 1:
            filterType = 'NOT DEPARTED'
            break;
          case 2:
            filterType = 'IN TRANSIT'
            break;
          case 3:
            filterType = 'PORT TERMINAL'
            break;
          case 4:
            filterType = 'LAX ORIGIN'
            break;
          case 5:
            filterType = 'IN INLAND TRANSIT'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
    }


    if (filterOrder && filterOrder.length > 0) {
      const tempReverseArray = [...filterOrder].reverse();
        tempReverseArray.slice(-6).map((x: any, i: number) => {
          return result.push({
            label:
              <span style={{ cursor: 'text' }}>
                {`Search ${tempReverseArray.length <= 6 ? i + 1 : tempReverseArray.length - (5 - i)} ${x.label}: `}
                {String(renderValue(x))}
              </span>
          });
        })
    }
    

    if (isActiveMenu) {
      result.push({ label: '' });
      result.push({ label: <span style={{ cursor: 'text', color: 'blue', fontWeight: 'bold' }}>CLICK {<FontAwesomeIcon color={'orange'} icon={faFilter} />} TO RESET FILTER</span> })
    }

    return result;
  }

  function resetSearch() {
    if (jobMenuUrl.includes('common/joblistview')) {
      const rowSelector = '.data-row-selected';
      const currentRowSelections = document.querySelectorAll(rowSelector);
      if (currentRowSelections && currentRowSelections.length > 0) {
        currentRowSelections[0].classList.remove('data-row-selected');
        currentRowSelections[0].classList.add('data-row-selectable');
      }
    }

    //List 내부 Filter 초기화 하기 위해 추가
    if (jobMenuUrl.includes('/common/joblistview/btLoadPlan/load')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTLoadPlan-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

    else if (jobMenuUrl.includes('/common/joblistview/btOrder/order')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTOrder-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

    else if (jobMenuUrl.includes('/common/joblistview/btLocalDO/localDO')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTDo-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

    else if (jobMenuUrl.includes('/common/joblistview/OCBO/orchidBaro')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('OrchidBaro-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('OrchidBaro-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }
    }




    channel.publish('ifms-filter.forceclear');

    if (!standAloneInfo) {
      dispatch(replaceJobMenuMetadata({ url: jobMenuUrl, metadata: jobMenuMetadata }));
      dispatch(replaceScreenStackMetadata({ url: jobMenuUrl, metadata: jobMenuMetadata }));
    }
  }

  function setMenuVisible(v: boolean) {
    openRef.current = v;
    _setMenuVisible(v);
  }

  function onMenuItemSelected(item: IfmsMenuItem, e: React.MouseEvent) {
    //setMenuVisible(false);

    if (!item) {
      resetSearch();
    }
  }

  const appliedPopperStyles = {
    ...styles.popper,
    minWidth: '75px',

    /*paddingTop: 10*/
  };

  const items = getSearchTooltipLabel(filterOrderStorage, jobMenuUrl, screenFilter);

  /* reformat array by separator. This is done because of avoiding changes in tons of files. */
  const formatterItems = useMemo(() => splitMenuItemsIntoSections(items),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items])

  return (
    <>
      <PrimaryInverseButton
        name={`${jobMenuLabel}-resetSearchBtn`}
        ref={buttonRef}
        onMouseEnter={() => setMenuVisible(true)}
        onMouseLeave={() => setMenuVisible(false)}
        size="small"
        style={{ height: '12px', padding: '0px', fontSize: '9px' }}
        onClick={() => {
          if (isActiveMenu)
            resetSearch();
        }}
      >
        <FontAwesomeIcon size={'lg'} color={isActiveMenu ? !standAloneInfo ? 'orange' : '#FF6B35' : 'black'} icon={faFilter} />
        <span ref={setReferenceElement}></span>
      </PrimaryInverseButton>

      <Portal node={document && document.getElementById('ifms-app-bottom')}>
        {menuVisible &&
          <div
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onMouseMove={(e) => { e.stopPropagation() }}
            onMouseEnter={() => setMenuVisible(true)}
            onMouseLeave={() => setMenuVisible(false)}
            ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
            <div ref={menuContainerRef}>
              <Menu
                specialMenu='tooltip'
                items={formatterItems}
                mode="vertical"
                onMenuItemSelected={(item, e) => { onMenuItemSelected(item, e) }}
                subMenuStyle={{
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                }}
                menuStyle={(i: number) => ({
                  marginTop: -10,
                  marginBottom: -10
                })}
              />
              <div ref={setArrowElement} style={styles.arrow} />
            </div>
          </div>
        }
      </Portal>
    </>
  );
}

TooltipButton.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    separator: PropTypes.bool,
  })),
};

export default React.memo(TooltipButton);